<script lang="ts">
    import { navigation } from '@otto-ec/global-resources/navigation';
    import { untrack } from 'svelte';
    import { navigationLog } from '@shared/log.browser.ts';
    import type { PrimitiveLabels } from '@shared/tracking/TrackingLabels.ts';
    import type { DresonRuleResolveEvent, DresonRuleResolvedEvent, Page } from '@shared/types/Tilelist.ts';
    import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import { appendHeurekaFragment, clean, pagedUrl } from './Utils.browser.ts';

    /**
 *
 *
 *
 *
 *
 *
 *
 */

    type Checkpoint = {
        rule: string;
        page: Page;
        tsLink?: PrimitiveLabels;
        historic: true;
        entryRule?: string;
    };

    const { initialSession }: { initialSession: TileListInitialSession } = $props();
    const wiring = injectWiring();

    function checkpoint(rule: string, page: Page, tsLink?: DresonRuleResolveEvent['tsLink']): Checkpoint {
        return { rule, page: { ...page }, tsLink, historic: true, entryRule: wiring.entryRule ?? undefined };
    }

    function isLabelValue(value: unknown): boolean {
        const isScalar = (item: unknown) =>
            typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean';
        return isScalar(value) || (Array.isArray(value) && value.every(isScalar));
    }

    function isPrimitiveLabels(value: unknown): value is PrimitiveLabels {
        return (
            typeof value === 'object' &&
            value !== null &&
            !Array.isArray(value) &&
            Object.values(value).every(isLabelValue)
        );
    }

    function isPage(value: unknown): value is Page {
        if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
        return (
            Object.keys(value).every((key) => ['sortiertnach', 'l', 'o', 'c'].includes(key)) &&
            (!('sortiertnach' in value) || typeof value.sortiertnach === 'string') &&
            (!('l' in value) || typeof value.l === 'string') &&
            (!('o' in value) || typeof value.o === 'string') &&
            (!('c' in value) || typeof value.c === 'string')
        );
    }

    function isCheckpoint(value: unknown): value is Checkpoint {
        if (typeof value !== 'object' || value === null) return false;
        return (
            'rule' in value &&
            typeof value.rule === 'string' &&
            'page' in value &&
            isPage(value.page) &&
            'historic' in value &&
            value.historic === true &&
            (!('tsLink' in value) || value.tsLink === undefined || isPrimitiveLabels(value.tsLink))
        );
    }

    function urlFor(event: DresonRuleResolvedEvent): string {
        const url = event.redirect ?? pagedUrl(clean(event.page), location.pathname, location.search);
        return event.redirect && wiring.onex.isToggleActive('DA_KEEP_HEUREKA_SHEET_FRAGMENT')
            ? appendHeurekaFragment(url)
            : url;
    }

    async function navigate(url: string, history: 'push' | 'replace', state: Checkpoint): Promise<void> {
        try {
            await navigation.navigate(url, {
                history,
                state,
            });
        } catch (error: unknown) {
            navigationLog.error('Failed to update navigation history', error);
        }
    }

    function handleResolved(event: DresonRuleResolvedEvent): void {
        const url = urlFor(event);
        wiring.trackingService?.trackUrl(event.redirect ?? url);
        if (!event.historic) void navigate(url, 'push', checkpoint(event.rule, event.page, event.tsLink));
    }

    function handleNavigation(event: Parameters<Parameters<typeof navigation.onNavigate.subscribe>[0]>[0]): void {
        if (event.type !== 'popstate') return;
        const restored = event.destination.state;
        if (isCheckpoint(restored)) wiring.eventsOutgoing?.requestPageResolution({ ...restored, historic: true });
    }

    function subscribe() {
        void navigate(location.href, 'replace', checkpoint(initialSession.page.context.rule, initialSession.page.page));
        const unsubscribeResolved = wiring.eventsIncoming?.onPageResolved(handleResolved);
        const unsubscribeRestore = navigation.onNavigate.subscribe(handleNavigation);
        return () => {
            unsubscribeResolved?.();
            unsubscribeRestore();
        };
    }

    $effect(() => {
        const eventsIncoming = wiring.eventsIncoming;
        const eventsOutgoing = wiring.eventsOutgoing;
        if (!eventsIncoming || !eventsOutgoing) return;
        return untrack(subscribe);
    });
</script>
